import { Trans as _Trans } from "../../lib/i18n/Trans";
import { Chip } from "../../components/Chip";
import { createBaseMarkdownComponents } from "../../components/markdown/createBaseMarkdownComponents";
import { MarkdownContentContext } from "../../components/markdown/MarkdownContentContext";
import { MarkdownStyleContext } from "../../components/markdown/MarkdownStyleContext";
import { safeRehypeKatex } from "../../components/markdown/safeRehypeKatex";
import { useAnimatedText } from "../../components/markdown/useAnimatedText";
import { preserveLineBreaks, sanitizeContent } from "../../components/markdown/utils";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { visit } from "unist-util-visit";
const DEFAULT_ANIMATION_DURATION_SECONDS = 1;
const DEFAULT_DELIMITER = "";
function showUnsupportedDirective() {
  return tree => {
    visit(tree, ["textDirective"], node => {
      if (node.type === "textDirective") {
        // it's not a valid directive, so we'll leave it as plain text
        node.type = "text";
        node.value = `:${node.name}${node.children ? node.children.map(c => c.value).join("") : ""}`;
      }
    });
  };
}
/**
 * Renders agent message bodies from a Markdown `content` string. Supports the
 * full GitHub-flavored set — headings, lists, task lists, tables, blockquotes,
 * links, footnotes — plus fenced code (via CodeBlock), LaTeX math, CSV/JSON
 * pretty-printing, and Mermaid diagrams, with optional streaming-aware
 * rendering and animated text reveal.
 * @summary GitHub-flavored Markdown renderer for agent messages.
 */
export const Markdown = ({
  content,
  isStreaming = false,
  streamingState,
  textColor = "text-foreground",
  forcedTextSize,
  isLastMessage = false,
  compactSpacing = false,
  additionalMarkdownComponents,
  additionalMarkdownPlugins,
  canCopyQuotes = true,
  taskListVariant = "checkbox",
  enableAnimation = false,
  animationDurationSeconds = DEFAULT_ANIMATION_DURATION_SECONDS,
  delimiter = DEFAULT_DELIMITER,
  optimizeForStreaming = true
}) => {
  // Derive streaming state: explicit prop takes priority, otherwise derive from isStreaming boolean.
  // @TODO: remove isStreaming prop and use streamingState prop only
  const effectiveStreamingState = streamingState !== null && streamingState !== void 0 ? streamingState : isStreaming ? "streaming" : "none";
  const processedContent = useMemo(() => {
    let sanitized = sanitizeContent(content);
    if (compactSpacing) {
      sanitized = preserveLineBreaks(sanitized);
    }
    return sanitized;
  }, [content, compactSpacing]);

  // Animate text during streaming for a smooth reveal effect.
  const animatedContent = useAnimatedText(processedContent, enableAnimation ? effectiveStreamingState : "none", animationDurationSeconds, delimiter);
  const styleContextValue = useMemo(() => ({
    textColor,
    forcedTextSize,
    compactSpacing,
    canCopyQuotes,
    taskListVariant
  }), [textColor, forcedTextSize, compactSpacing, canCopyQuotes, taskListVariant]);

  // Note on re-renderings. A lot of effort has been put into preventing rerendering across markdown
  // AST parsing rounds (happening at each token being streamed).
  //
  // All base components are React.memo'd with sameNodePosition custom comparison.
  // During streaming, unchanged nodes (same AST position) skip re-rendering entirely.
  // Style props flow through MarkdownStyleContext, which bypasses memo when values change.
  //
  // Minimal test whenever editing this code: ensure that code block content of a streaming message
  // can be selected without blinking.

  // When optimizeForStreaming is false, plain (non-memo) block components are used so
  // content edits at unchanged AST positions still re-render (e.g. file preview).

  const baseMarkdownComponents = useMemo(() => createBaseMarkdownComponents(optimizeForStreaming), [optimizeForStreaming]);

  // Merge base components with additional directive components.
  const markdownComponents = useMemo(() => ({
    ...baseMarkdownComponents,
    ...additionalMarkdownComponents
  }), [baseMarkdownComponents, additionalMarkdownComponents]);
  const markdownPlugins = useMemo(() => [remarkDirective, remarkGfm, [remarkMath, {
    singleDollarTextMath: false
  }], ...(additionalMarkdownPlugins || []), showUnsupportedDirective], [additionalMarkdownPlugins]);
  const rehypePlugins = useMemo(() => [[safeRehypeKatex, {
    output: "mathml"
  }]], []);
  const contentContextValue = useMemo(() => ({
    content: processedContent,
    isStreaming: effectiveStreamingState === "streaming",
    isLastMessage
  }), [processedContent, effectiveStreamingState, isLastMessage]);
  try {
    return /*#__PURE__*/React.createElement("div", {
      className: "w-full"
    }, /*#__PURE__*/React.createElement(MarkdownStyleContext.Provider, {
      value: styleContextValue
    }, /*#__PURE__*/React.createElement(MarkdownContentContext.Provider, {
      value: contentContextValue
    }, /*#__PURE__*/React.createElement(ReactMarkdown, {
      linkTarget: "_blank",
      components: markdownComponents,
      remarkPlugins: markdownPlugins,
      rehypePlugins: rehypePlugins
    }, animatedContent))));
  } catch (_error) {
    return /*#__PURE__*/React.createElement("div", {
      className: "w-full"
    }, /*#__PURE__*/React.createElement(Chip, {
      color: "warning"
    }, /*#__PURE__*/React.createElement(_Trans,
    /** i18n */
    {
      id: "oej6Xf",
      message: "There was an error parsing this markdown content"
    })), processedContent);
  }
};
//# sourceMappingURL=Markdown.js.map